import React, { useState } from 'react';
import { 
  Phone, Mail, Calendar, User, IndianRupee, 
  MoreVertical, Star, Clock, MessageSquare 
} from 'lucide-react';

const LeadKanbanBoard = ({ leads, stages, onUpdateStage, onViewDetails, onEditLead, onUpdateLead, onAssignLead }) => {
  const [draggedLead, setDraggedLead] = useState(null);

  const getLeadsByStage = (stage) => {
    return leads.filter(lead => lead.stage === stage);
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    
    // Check if target stage is read-only
    const stageObj = stages.find(s => s.value === targetStage);
    if (stageObj?.isReadOnly) {
      alert('You can only view this stage, not move leads to it');
      setDraggedLead(null);
      return;
    }
    
    if (draggedLead && draggedLead.stage !== targetStage) {
      onUpdateStage(draggedLead.id, targetStage);
    }
    setDraggedLead(null);
  };

  const getStageColor = (stage) => {
    const colors = {
      'new': '#3b82f6',
      'contacted': '#8b5cf6',
      'qualified': '#06b6d4',
      'consultation_scheduled': '#10b981',
      'consultation_completed': '#f59e0b',
      'proposal_sent': '#f97316',
      'agreement_sent': '#ef4444',
      'agreement_signed': '#14b8a6',
      'payment_partial': '#84cc16',
      'payment_full': '#22c55e',
      'lost': '#6b7280',
      'nurture': '#a855f7'
    };
    return colors[stage] || '#6b7280';
  };

  const getTemperatureIcon = (temp) => {
    if (temp === 'hot') return '🔥';
    if (temp === 'warm') return '☀️';
    return '❄️';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    if (diff < 7) return `${diff}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="kanban-container">
      <div className="kanban-board">
        {stages.map(stage => (
          <div
            key={stage.value}
            className="kanban-column"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.value)}
          >
            <div className="kanban-column-header" style={{ borderTopColor: getStageColor(stage.value) }}>
              <div className="kanban-header-content">
                <h3 className="kanban-column-title">
                  {stage.label}
                  {stage.isReadOnly && (
                    <span style={{ 
                      fontSize: '0.75rem', 
                      marginLeft: '0.5rem', 
                      color: '#f59e0b',
                      fontWeight: 'normal'
                    }}>
                      👁️ View Only
                    </span>
                  )}
                </h3>
                <span className="kanban-count">{getLeadsByStage(stage.value).length}</span>
              </div>
            </div>
            
            <div className="kanban-cards">
              {getLeadsByStage(stage.value).map(lead => (
                <div
                  key={lead.id}
                  className="kanban-card"
                  draggable
                  onDragStart={(e) => handleDragStart(e, lead)}
                  onClick={() => onViewDetails(lead)}
                >
                  <div className="kanban-card-header">
                    <div className="kanban-card-title-row">
                      <h4 className="kanban-card-title">{lead.name}</h4>
                      <span className="kanban-temp-badge">{getTemperatureIcon(lead.temperature)}</span>
                    </div>
                    {lead.lead_score >= 70 && (
                      <div className="kanban-score-badge high-score">
                        <Star size={12} /> {lead.lead_score}
                      </div>
                    )}
                  </div>
                  
                  <div className="kanban-card-body">
                    {lead.service_type && (
                      <div className="kanban-info-row">
                        <span className="kanban-label">Service:</span>
                        <span className="kanban-value">{lead.service_type}</span>
                      </div>
                    )}
                    {(lead.display_country || lead.destination_country) && (
                      <div className="kanban-info-row">
                        <span className="kanban-label">Destination:</span>
                        <span className="kanban-value">{lead.display_country || lead.destination_country}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="kanban-card-footer">
                    <div className="kanban-meta">
                      {lead.contact_attempts > 0 && (
                        <span className="kanban-meta-item" title="Contact attempts">
                          <Phone size={14} /> {lead.contact_attempts}
                        </span>
                      )}
                      {lead.assigned_to && (
                        <span className="kanban-meta-item" title="Assigned to">
                          <User size={14} /> {lead.assigned_to}
                        </span>
                      )}
                      {lead.created_at && (
                        <span className="kanban-meta-item" title="Created">
                          <Clock size={14} /> {formatDate(lead.created_at)}
                        </span>
                      )}
                      {lead.payment_info && lead.payment_info.amount_paid > 0 && (
                        <span className="kanban-payment" style={{ marginLeft: 'auto' }}>
                          <IndianRupee size={14} />
                          ₹{lead.payment_info.amount_paid}
                        </span>
                      )}
                    </div>
                    <div className="kanban-actions">
                      {onAssignLead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssignLead(lead);
                          }}
                          className="kanban-btn kanban-btn-assign"
                        >
                          Assign
                        </button>
                      )}
                      {onUpdateLead && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onUpdateLead(lead);
                          }}
                          className="kanban-btn kanban-btn-update"
                        >
                          Update
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {getLeadsByStage(stage.value).length === 0 && (
                <div className="kanban-empty">
                  No leads in this stage
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeadKanbanBoard;
